import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { PaymentOrder, PaymentOrderItem } from '../../types';
import Button from './Button';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useProviders } from '../../hooks/useProviders';
import { useBanks } from '../../hooks/useBanks';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import {
  OrderInfoSection,
  ProviderPaymentSection,
  PaymentOrderItemsTable,
  TotalsSection,
  AdditionalInfoSection,
  AuthorizationSection,
  StatusPersonnelSection,
} from './PaymentOrderSections';

interface PaymentOrderFormProps {
  paymentOrder?: PaymentOrder;
  onSubmit: (data: Partial<PaymentOrder>) => void;
  onCancel: () => void;
}

const PaymentOrderForm: React.FC<PaymentOrderFormProps> = ({ 
  paymentOrder, 
  onSubmit, 
  onCancel 
}) => {
  const { data: currencies } = useCurrencies();
  const codePrefixQueryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(codePrefixQueryOptions);

  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [orderSeqNumber, setOrderSeqNumber] = useState<string>('001');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue
  } = useForm<PaymentOrder>({
    defaultValues: paymentOrder ? {
      ...paymentOrder,
      date: formatDateForInput(paymentOrder.date),
      // API stores authorizedBy flat; rebuild the nested form shape
      authorizedBy: paymentOrder.authorizedBy || {
        name: (paymentOrder as any).authorizedByName || '',
        jobTitle: (paymentOrder as any).authorizedByJobTitle || '',
        signature: (paymentOrder as any).authorizedBySignature || '',
      },
    } : {
      orderNumber: '',
      date: new Date().toISOString().split('T')[0],
      referenceNo: '',
      providerId: '',
      paymentMethod: 'bank_transfer',
      bankId: '',
      checkNumber: '',
      items: [
        {
          id: '1',
          no: 1,
          details: '',
          unit: '',
          qty: 1,
          unitPrice: 0,
          total: 0
        }
      ],
      subtotal: 0,
      taxes: 0,
      discount: 0,
      total: 0,
      currency: 'EGP',
      exchangeRate: undefined,
      status: 'pending',
      requestedBy: '',
      approvedBy: '',
      paidBy: '',
      comments: '',
      additionalInstructions: '',
      authorizedBy: {
        signature: '',
        name: '',
        jobTitle: ''
      }
    }
  });

  useEffect(() => {
    if (paymentOrder?.orderNumber && (codePrefixes as any[]).length > 0) {
      const matchingPrefix = (codePrefixes as any[]).find((p: any) =>
        paymentOrder.orderNumber.startsWith(p.prefix)
      );
      if (matchingPrefix) {
        setSelectedCodePrefix(matchingPrefix.id);
        setOrderSeqNumber(paymentOrder.orderNumber.replace(matchingPrefix.prefix, ''));
      }
    }
  }, [paymentOrder, codePrefixes]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');
  const selectedCurrency = watch('currency');
  const paymentMethod = watch('paymentMethod');
  const discount = watch('discount') || 0;

  const { data: providersData } = useProviders();
  const { data: banksData } = useBanks();

  const providerOptions = useMemo(() => {
    if (!providersData || providersData.length === 0) return [];
    return providersData.map((provider: any) => ({
      value: provider.documentId || provider.id,
      label: provider.name
    }));
  }, [providersData]);

  const bankOptions = useMemo(() => {
    if (!banksData || banksData.length === 0) return [];
    return banksData.map((bank: any) => ({
      value: bank.documentId || bank.id,
      label: `${bank.name} (${bank.accountNumber || ''})`
    }));
  }, [banksData]);

  const paymentMethodOptions = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'cash', label: 'Cash' },
    { value: 'credit_card', label: 'Credit Card' }
  ];

  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) return [];
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.name || currency.code || 'Unknown'} (${currency.code || ''})`
      }));
  }, [currencies]);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    watchedItems?.forEach((item, index) => {
      if (item.qty && item.unitPrice) {
        const total = item.qty * item.unitPrice;
        setValue(`items.${index}.total`, total);
      }
    });
  }, [watchedItems, setValue]);

  useEffect(() => {
    const subtotal = watchedItems?.reduce((sum, item) => sum + (item.total || 0), 0) || 0;
    const taxes = subtotal * 0.14;
    const total = subtotal + taxes - discount;

    setValue('subtotal', subtotal);
    setValue('taxes', taxes);
    setValue('total', Math.max(0, total));
  }, [watchedItems, discount, setValue]);

  useEffect(() => {
    if (!paymentOrder) {
      const timestamp = Date.now().toString().slice(-6);
      setValue('orderNumber', `PO-${timestamp}`);
    }
  }, [paymentOrder, setValue]);

  const addItem = () => {
    const newItem: PaymentOrderItem = {
      id: Date.now().toString(),
      no: fields.length + 1,
      details: '',
      unit: '',
      qty: 1,
      unitPrice: 0,
      total: 0
    };
    append(newItem);
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      setTimeout(() => {
        fields.forEach((_, i) => {
          if (i > index) {
            setValue(`items.${i}.no`, i + 1);
          }
        });
      }, 0);
    }
  };

  const onFormSubmit = (data: PaymentOrder) => {
    const processedData = {
      ...data,
      date: formatDateForAPI(data.date),
      items: data.items.map((item, index) => ({
        ...item,
        id: item.id || Date.now().toString() + index,
        no: index + 1
      }))
    };
    onSubmit(processedData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {paymentOrder ? 'Edit Payment Order' : 'Create Payment Order'}
          </h1>
          <p className="text-gray-600">
            {paymentOrder ? 'Update payment order details' : 'Create a new payment order'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        <OrderInfoSection
          register={register}
          errors={errors}
          codePrefixes={codePrefixes as any[]}
          selectedCodePrefix={selectedCodePrefix}
          setSelectedCodePrefix={setSelectedCodePrefix}
          orderSeqNumber={orderSeqNumber}
          setOrderSeqNumber={setOrderSeqNumber}
          setValue={setValue}
          isEditing={!!paymentOrder}
        />

        <ProviderPaymentSection
          register={register}
          errors={errors}
          providerOptions={providerOptions}
          bankOptions={bankOptions}
          currencyOptions={currencyOptions}
          paymentMethodOptions={paymentMethodOptions}
          paymentMethod={paymentMethod}
          selectedCurrency={selectedCurrency}
        />

        <PaymentOrderItemsTable
          register={register}
          fields={fields}
          watchedItems={watchedItems}
          addItem={addItem}
          removeItem={removeItem}
        />

        <TotalsSection register={register} errors={errors} />
        <AdditionalInfoSection register={register} />
        <AuthorizationSection register={register} errors={errors} />
        <StatusPersonnelSection register={register} errors={errors} statusOptions={statusOptions} />

        <div className="flex justify-end space-x-4">
          <Button type="button" onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : paymentOrder ? 'Update Payment Order' : 'Create Payment Order'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PaymentOrderForm;
